import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, X, FileImage, FileText, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLLECTION_TYPES = [
  "Magic: The Gathering",
  "Pokémon",
  "Other TCG (Yu-Gi-Oh!, Lorcana, etc.)",
  "High-Value Singles",
  "Sealed Products",
  "Mixed Collection",
];

const MAX_FILES = 8;
const MAX_MB = 10;

const SellerForm = () => {
  const [form, setForm] = useState({
    name: "",
    contact: "",
    collection_type: "",
    size_value: "",
    location: "",
    description: "",
  });
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successId, setSuccessId] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addFiles = (incoming) => {
    const next = [...files];
    for (const f of incoming) {
      if (next.length >= MAX_FILES) break;
      if (f.size > MAX_MB * 1024 * 1024) {
        toast.error(`${f.name} is over ${MAX_MB}MB`);
        continue;
      }
      next.push(f);
    }
    setFiles(next);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Your name is required.";
    if (!form.contact.trim()) e.contact = "Add an email or phone number so Ashton can reach you.";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contact.trim()) && !/^[+()\-.\s\d]{7,}$/.test(form.contact.trim()))
      e.contact = "Enter a valid email address or phone number.";
    if (!form.collection_type) e.collection_type = "Choose the closest collection type.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      files.forEach((f) => data.append("files", f));
      const res = await axios.post(`${API}/sell-offer`, data);
      setSuccessId(res.data.submission_id);
      toast.success("Offer request sent — Ashton will be in touch within 24 hours.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or email ashton@aardvarksvault.com.");
    } finally {
      setSubmitting(false);
    }
  };

  if (successId) {
    return (
      <div className="card-frame foil-sheen p-10 text-center" data-testid="seller-form-success">
        <CheckCircle2 className="mx-auto text-mint" size={52} />
        <h3 className="mt-6 font-serif text-2xl font-bold text-cream">Your collection is in the Vault queue</h3>
        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-cream/75">
          Thanks, {form.name.split(" ")[0]}. Your request has been sent directly to Ashton. Expect a fair,
          market-based response within 24 hours at the contact you provided.
        </p>
        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-gold" data-testid="seller-form-success-ref">
          Reference · {successId.slice(0, 8).toUpperCase()}
        </p>
        <button
          className="btn-ghost mt-8"
          data-testid="seller-form-again-button"
          onClick={() => {
            setSuccessId(null);
            setForm({ name: "", contact: "", collection_type: "", size_value: "", location: "", description: "" });
            setFiles([]);
          }}
        >
          Submit another collection
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="card-frame p-6 sm:p-10" data-testid="seller-form" noValidate>
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="sf-name" className="eyebrow mb-2 block">Your Name *</label>
          <input id="sf-name" data-testid="seller-form-name-input" className="field" value={form.name} onChange={set("name")} placeholder="Evelyn Cardwell" />
          {errors.name && <p className="mt-2 text-xs text-red-400" data-testid="seller-form-name-error">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="sf-contact" className="eyebrow mb-2 block">Email or Phone *</label>
          <input id="sf-contact" data-testid="seller-form-contact-input" className="field" value={form.contact} onChange={set("contact")} placeholder="you@example.com" />
          {errors.contact && <p className="mt-2 text-xs text-red-400" data-testid="seller-form-contact-error">{errors.contact}</p>}
        </div>
        <div>
          <label htmlFor="sf-type" className="eyebrow mb-2 block">Collection Type *</label>
          <select id="sf-type" data-testid="seller-form-type-select" className="field" value={form.collection_type} onChange={set("collection_type")}>
            <option value="">Select a type…</option>
            {COLLECTION_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          {errors.collection_type && <p className="mt-2 text-xs text-red-400" data-testid="seller-form-type-error">{errors.collection_type}</p>}
        </div>
        <div>
          <label htmlFor="sf-size" className="eyebrow mb-2 block">Approx. Size or Value</label>
          <input id="sf-size" data-testid="seller-form-size-input" className="field" value={form.size_value} onChange={set("size_value")} placeholder="e.g. 5,000 cards or ~$8,000" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="sf-location" className="eyebrow mb-2 block">Your Location</label>
          <input id="sf-location" data-testid="seller-form-location-input" className="field" value={form.location} onChange={set("location")} placeholder="City, State — we buy nationwide" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="sf-description" className="eyebrow mb-2 block">Tell Us About the Collection</label>
          <textarea
            id="sf-description"
            data-testid="seller-form-description-input"
            className="field min-h-[130px] resize-y"
            value={form.description}
            onChange={set("description")}
            placeholder="Sets, eras, highlights (chase cards, graded slabs, sealed boxes), condition…"
          />
        </div>
        <div className="sm:col-span-2">
          <span className="eyebrow mb-2 block">Photos or Card List (up to {MAX_FILES} files, {MAX_MB}MB each)</span>
          <div
            role="button"
            tabIndex={0}
            aria-label="Upload photos or a card list"
            data-testid="seller-form-upload-zone"
            onClick={() => fileInput.current?.click()}
            onKeyDown={(e) => e.key === "Enter" && fileInput.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles([...e.dataTransfer.files]); }}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed px-6 py-10 text-center transition-colors duration-200 ${
              dragging ? "border-mint bg-mint/10" : "border-gold/30 bg-navy/40 hover:border-gold/60"
            }`}
          >
            <UploadCloud className="text-gold" size={34} />
            <p className="mt-3 text-sm text-cream/80">Drag & drop photos or a card list, or click to browse</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">JPG · PNG · WEBP · PDF · TXT · CSV</p>
          </div>
          <input
            ref={fileInput}
            type="file"
            multiple
            accept="image/*,.pdf,.txt,.csv,.md"
            className="hidden"
            data-testid="seller-form-file-input"
            onChange={(e) => { addFiles([...e.target.files]); e.target.value = ""; }}
          />
          {files.length > 0 && (
            <ul className="mt-4 flex flex-wrap gap-2" data-testid="seller-form-file-list">
              {files.map((f, i) => (
                <li key={i} className="flex items-center gap-2 rounded-full border border-mint/30 bg-mint/5 px-3 py-1.5 text-xs text-cream/85">
                  {f.type.startsWith("image/") ? <FileImage size={13} className="text-mint" /> : <FileText size={13} className="text-mint" />}
                  <span className="max-w-[160px] truncate">{f.name}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${f.name}`}
                    data-testid={`seller-form-file-remove-${i}`}
                    onClick={() => setFiles(files.filter((_, j) => j !== i))}
                    className="text-cream/50 hover:text-red-400"
                  >
                    <X size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-sm text-xs leading-relaxed text-cream/50">
          Submissions go straight to Ashton at ashton@aardvarksvault.com. Your details are never shared or sold.
        </p>
        <button type="submit" className="btn-gold" disabled={submitting} data-testid="seller-form-submit-button">
          {submitting ? <Loader2 className="animate-spin" size={16} /> : null}
          {submitting ? "Sending…" : "Get a Collection Offer"}
        </button>
      </div>
    </form>
  );
};

export default SellerForm;
